import React, { useState } from 'react';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import SimpleBar from 'simplebar-react';
import { chartColors } from '../../constants/layout';

const getFlexDirection = position => {
	return ['left', 'right'].includes(position) ? 'row' : 'column';
};

const getContentOrder = position => {
	if (position === 'left' || position === 'top') {
		return { legendOrder: 1, chartOrder: 2 };
	} else if (position === 'right' || position === 'bottom') {
		return { legendOrder: 2, chartOrder: 1 };
	}
};

const formatNumberWithThousandSeparator = number => {
	let [integerPart, decimalPart] = number.toString().split('.');
	integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
	decimalPart = decimalPart ? `,${decimalPart}` : '';
	return `${integerPart}${decimalPart}`;
};

const ODoughnutChart = React.memo(
	({
		title,
		subtitle,
		width = '100%',
		height = '100%',
		seriesChart,
		labelsChart,
		labelsHeaderChart,
		colorsChart,
		iconChart,
		legendPosition,
		with_total,
		um = '',
		selectedFilter,
		onFilterChange,
	}) => {
		const total = seriesChart.reduce((acc, curr) => acc + curr, 0);
		const percentages = seriesChart.map(value =>
			((value / total) * 100).toFixed(1).replace('.', ',')
		);
		const formattedSeriesChart = seriesChart.map(value =>
			formatNumberWithThousandSeparator(value)
		);

		const handleFilterClick = filter => {
			if (onFilterChange) {
				setSelectedFilter(filter);
				onFilterChange(filter); // Llama la función del componente padre
			}
		};

		const options = {
			chart: {
				width: width,
				type: 'donut',
			},
			colors: colorsChart || chartColors.default,
			labels: labelsChart,
			legend: {
				show: false,
			},
			dataLabels: {
				enabled: false,
			},
			tooltip: {
				y: {
					formatter: function (value, { dataPointIndex }) {
						return formattedSeriesChart[dataPointIndex] + ' ' + um;
					},
				},
			},
			responsive: [
				{
					breakpoint: width,
					options: {
						chart: {
							width: width,
							height: height,
						},
					},
				},
			],
		};

		// Generar las filas de la tabla personalizada
		const legendRows = (
			<div
				className='table-responsive table-card fs-12'
				style={{ marginTop: '0px' }}
			>
				<table className='table table-sm' style={{ marginBottom: '2px' }}>
					<thead className='table-light'>
						<tr>
							{labelsHeaderChart &&
								labelsHeaderChart.length > 0 &&
								labelsHeaderChart.map((label, index) => (
									<th key={index} className='text-muted'>
										{label}
									</th>
								))}
						</tr>
					</thead>
					<tbody>
						{labelsChart.map((label, index) => (
							<tr key={index}>
								<td
									className='text-muted fw-semibold'
									style={{ padding: '4px 8px' }}
								>
									<i
										className={`${
											iconChart && iconChart[index]
												? iconChart[index]
												: Icons.checkCircle
										} align-middle me-2`}
										style={{
											color: colorsChart
												? colorsChart[index]
												: chartColors.default[index],
										}}
									></i>
									{label}
								</td>
								<td
									className='text-muted text-right'
									style={{ padding: '4px' }}
								>
									{formattedSeriesChart[index]}
								</td>
								<td
									className='text-muted fw-semibold'
									style={{ padding: '4px' }}
								>
									{percentages[index]}%
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);

		return (
			<Card className='m-0 h-100'>
				<CardHeader className='p-2 align-items-center d-flex'>
					<h5 className='card-title mb-0 flex-grow-1'>{title}</h5>
					<div className='btn-box-dashboard'>
						<Button
							color={selectedFilter === '1Y' ? 'primary' : 'secondary'}
							size='sm'
							className={
								selectedFilter === '1Y'
									? 'btn-soft-primary me-1'
									: 'btn-soft-secondary me-1'
							}
							onClick={() => onFilterChange('1Y')}
							title='1 Año'
						>
							1A
						</Button>
						<Button
							color={selectedFilter === '6M' ? 'primary' : 'secondary'}
							size='sm'
							className={
								selectedFilter === '6M'
									? 'btn-soft-primary me-1'
									: 'btn-soft-secondary me-1'
							}
							onClick={() => onFilterChange('6M')}
							title='6 Meses'
						>
							6M
						</Button>
						<Button
							color={selectedFilter === '3M' ? 'primary' : 'secondary'}
							size='sm'
							className={
								selectedFilter === '3M'
									? 'btn-soft-primary me-1'
									: 'btn-soft-secondary me-1'
							}
							onClick={() => onFilterChange('3M')}
							title='3 Meses'
						>
							3M
						</Button>
						<Button
							color={selectedFilter === '1M' ? 'primary' : 'secondary'}
							size='sm'
							className={
								selectedFilter === '1M'
									? 'btn-soft-primary'
									: 'btn-soft-secondary'
							}
							onClick={() => onFilterChange('1M')}
							title='1 Mes'
						>
							1M
						</Button>
					</div>
				</CardHeader>
				<CardBody style={{ paddingTop: '0px' }}>
					<div
						style={{
							display: 'flex',
							flexDirection: getFlexDirection(legendPosition),
						}}
					>
						{getContentOrder(legendPosition).legendOrder === 1 && (
							<div style={{ flexGrow: 0, flexBasis: '70%' }}>
								{legendRows}
							</div>
						)}
						<div id='chart' style={{ flexGrow: 1 }}>
							{with_total && (
								<div className='d-flex justify-content-center align-items-center'>
									<h2 className='me-3 ff-secondary mb-0'>{total}</h2>
									<div>
										<p className='text-muted mb-0'>{subtitle}</p>
									</div>
								</div>
							)}
							<ReactApexChart
								options={options}
								series={seriesChart}
								type='donut'
								width={width || '100%'}
							/>
						</div>
						{getContentOrder(legendPosition).legendOrder === 2 && (
							<div style={{ flexGrow: 0, flexBasis: '70%' }}>
								{legendRows}
							</div>
						)}
					</div>
				</CardBody>
			</Card>
		);
	}
);

export default ODoughnutChart;
